import { useEffect, useState } from 'react'
import { IHubFolder } from 'types'
import { loadExpanded, saveExpanded } from 'components/sidebar/components/folders/viewState'

export interface ExpandedFolders {
  /** False until this hub's saved state has been read; nothing is expanded until then. */
  loaded: boolean
  expanded: Set<string>
  toggle: (folderId: string) => void
  /** Opens every folder given; already-open ones stay open. */
  expand: (folderIds: string[]) => void
  /** Closes every folder. */
  collapseAll: () => void
}

interface HubExpanded {
  hubId: string
  ids: Set<string>
}

const NOTHING_EXPANDED: Set<string> = new Set()

/**
 * The open folders in the hub's sidebar, remembered per hub in localStorage.
 *
 * Read once the hub has loaded (`ready`), and pruned then against the hub's folders, so folders
 * deleted since the last visit don't pile up in storage. Reading earlier would prune against the
 * previous hub's folders, or none, and wipe the saved state.
 */
export const useExpandedFolders = (hubId: string, folders: IHubFolder[], ready: boolean): ExpandedFolders => {
  const [state, setState] = useState<HubExpanded | null>(null)
  const loaded = state !== null && state.hubId === hubId

  useEffect(() => {
    if (!ready || !hubId || loaded) return
    const known = new Set(folders.map((f) => f.publicId))
    setState({ hubId, ids: new Set([...loadExpanded(hubId)].filter((id) => known.has(id))) })
  }, [hubId, ready, loaded])

  // Every change is saved, including the pruned set just loaded.
  useEffect(() => {
    if (state) saveExpanded(state.hubId, state.ids)
  }, [state])

  /** `update` returns its argument to change nothing. */
  const change = (update: (ids: Set<string>) => Set<string>): void =>
    setState((prev) => {
      if (!prev || prev.hubId !== hubId) return prev
      const ids = update(prev.ids)
      return ids === prev.ids ? prev : { hubId, ids }
    })

  const toggle = (folderId: string): void =>
    change((ids) => {
      const next = new Set(ids)
      if (next.has(folderId)) next.delete(folderId)
      else next.add(folderId)
      return next
    })

  const expand = (folderIds: string[]): void =>
    change((ids) => (folderIds.every((id) => ids.has(id)) ? ids : new Set([...ids, ...folderIds])))

  const collapseAll = (): void => change((ids) => (ids.size === 0 ? ids : new Set()))

  return { loaded, expanded: loaded ? state.ids : NOTHING_EXPANDED, toggle, expand, collapseAll }
}
